"use client";

import { Box, Typography } from "@mui/material";
import { theme } from "@/lib/theme";
import Image from "next/image";
import { motion, useInView } from "framer-motion";
import { useRef, useMemo } from "react";

interface LargeQuoteProps {
  children: React.ReactNode;
  author?: string;
  authorTitle?: string;
  fullWidth?: boolean;
  image?: string;
  color?: "black" | "purple";
  showQuotes?: boolean;
}

export const LargeQuote = ({
  children,
  author,
  authorTitle,
  fullWidth = false,
  image,
  color = "black",
  showQuotes = true,
}: LargeQuoteProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  // Extract text content and split into words
  const words = useMemo(() => {
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === "string") {
        return node;
      }
      if (Array.isArray(node)) {
        return node.map(extractText).join("");
      }
      if (node && typeof node === "object" && "props" in node) {
        const element = node as { props?: { children?: React.ReactNode } };
        if (element.props?.children) {
          return extractText(element.props.children);
        }
      }
      return "";
    };

    const text = extractText(children);
    return text.split(/\s+/).filter((word) => word.length > 0);
  }, [children]);

  return (
    <Box
      ref={ref}
      sx={{
        marginY: {
          xs: 4, // 32px mobile
          sm: 6, // 48px desktop
        },
      }}
    >
      {image && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            marginBottom: {
              xs: 2, // Positive margin on mobile to prevent overlap
              sm: -2, // Negative margin on desktop for tighter spacing
            },
            position: "relative",
          }}
        >
          <Image
            src={image}
            alt=""
            width={300}
            height={150}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </Box>
      )}
      <Box
        sx={{
          paddingLeft: fullWidth ? 0 : 3, // 24px
          borderLeft: fullWidth ? "none" : `2px solid ${theme.colors.gray[300]}`,
          textAlign: fullWidth ? "center" : "left",
        }}
      >
        <Typography
          variant="h4"
          component="div"
          sx={{
            marginTop: 0,
            color: color === "purple" ? theme.colors.purple : theme.colors.black,
            "& > p": {
              display: "inline",
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: "inherit",
              margin: 0,
              color: "inherit",
            },
          }}
        >
          {showQuotes ? (
            <>
              <motion.span
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{
                  duration: 0.3,
                  delay: 0,
                }}
                style={{ display: "inline-block" }}
              >
                {"\u201C"}
              </motion.span>
              {words.map((word, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: (index + 1) * 0.05,
                  }}
                  style={{
                    display: "inline-block",
                    marginRight: index === words.length - 1 ? "0" : "0.25em",
                  }}
                >
                  {word}
                </motion.span>
              ))}
              <motion.span
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{
                  duration: 0.3,
                  delay: (words.length + 1) * 0.05,
                }}
                style={{ display: "inline-block" }}
              >
                {"\u201D"}
              </motion.span>
            </>
          ) : (
            children
          )}
        </Typography>
        {author && (
          <Box
            sx={{
              marginTop: 4, // 32px
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: theme.fonts.sans,
                fontSize: "16px",
                fontWeight: theme.fonts.weights.bold,
                color: theme.colors.black,
              }}
            >
              {author}
            </Typography>
            {authorTitle && (
              <Typography
                component="span"
                sx={{
                  fontFamily: theme.fonts.sans,
                  fontSize: "16px",
                  fontWeight: theme.fonts.weights.normal,
                  color: theme.colors.gray[500],
                }}
              >
                , {authorTitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
